import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { problemCode } from '@up2d8/api-client';
import type {
  AccessStatus,
  ActivationCodeResponse,
  Deactivation,
  PushStatusList,
} from '@up2d8/contracts';
import { formatDate, formatDateTime } from '../../shared/format';
import { useSession } from '../../shared/session';
import {
  AdminIcon,
  Badge,
  Btn,
  CodeDisplay,
  DefinitionList,
  Field,
  Input,
  Row,
  Sect,
  Segmented,
  type BadgeTone,
} from '../admin-kit';
import { useMelding } from './people-ui';

/**
 * De tab "Toegang" bij een medewerker: kan hij binnen, en tot wanneer? (Sinds BHR-2 frame M2.)
 *
 * Twee handelingen die verder reiken dan dit scherm staan hier bij elkaar: een
 * activatiecode maken (dat is meteen de pincodereset) en iemand uit dienst zetten. Dat
 * tweede is een harde grens — binnen dertig seconden werkt zijn telefoon niet meer.
 *
 * Twee secties uit M2: "Account" bovenaan en "Uit dienst" onderaan; daartussen zet het paneel
 * de rollen en de leidinggevende.
 */
export function PersonAccountSection({
  personId,
  employeeNumber,
  onChanged,
}: {
  personId: string;
  employeeNumber: string | null;
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { api, has } = useSession();
  const melding = useMelding();
  const queryClient = useQueryClient();

  const [issuedCode, setIssuedCode] = useState<ActivationCodeResponse | null>(null);

  const canReset = has('identity.credential.reset');
  const canSeePush = has('notifications.push.view');

  const access = useQuery({
    queryKey: ['identity', 'access', personId],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/accounts/status', {
        params: { query: { personIds: personId } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { items: AccessStatus[] }).items[0] ?? null;
    },
  });

  /**
   * Op hoeveel toestellen staan zijn meldingen aan?
   *
   * Dit is het antwoord op "waarom reageert hij niet": zonder toestel krijgt hij niets op zijn
   * telefoon en moet hij de app zelf openen. Alleen een aantal — welke telefoon iemand heeft,
   * is zijn zaak. Staat de module `notifications` uit, dan mislukt dit stil en verdwijnt de
   * regel; dat is beter dan een foutmelding over iets dat er niet is.
   */
  const push = useQuery({
    queryKey: ['notifications', 'push-status', personId],
    enabled: canSeePush,
    retry: false,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/push/status', {
        params: { query: { personIds: personId } },
      });
      return (data as PushStatusList | undefined)?.items[0]?.activeDevices ?? null;
    },
  });

  const issueCode = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST('/api/v1/accounts/{personId}/activation-code', {
        params: { path: { personId } },
      });
      if (error || !data) throw error ?? new Error('Aanmaken mislukt');
      return data as ActivationCodeResponse;
    },
    onSuccess: async (code) => {
      setIssuedCode(code);
      await queryClient.invalidateQueries({ queryKey: ['identity'] });
      await queryClient.invalidateQueries({ queryKey: ['directory'] });
      onChanged();
    },
    onError: (error) =>
      melding.fout(
        problemCode(error) === 'CONFLICT' ? t('access.codeImpossible') : t('access.codeFailed'),
      ),
  });

  // De 2FA van deze medewerker terugzetten, bv. na een verloren telefoon (FBK-12).
  const canManageAccounts = has('identity.account.manage');
  const resetMfa = useMutation({
    mutationFn: async () => {
      const { error } = await api.DELETE('/api/v1/accounts/{personId}/mfa', {
        params: { path: { personId } },
      });
      if (error) throw error;
    },
    onSuccess: () => melding.ok(t('mfa.resetDone')),
    onError: () => melding.fout(t('errors.generic')),
  });

  const status = access.data?.status ?? 'none';
  const lastLogin = access.data?.lastLoginAt ?? null;

  const rijen: [string, ReactNode][] = [
    [t('access.status'), <AccessBadge key="status" status={status} />],
    [t('beheer.medewerkers.lastLogin'), lastLogin ? formatDateTime(lastLogin) : '—'],
  ];
  if (canSeePush && push.data !== null && push.data !== undefined) {
    rijen.push([
      t('beheer.medewerkers.push'),
      push.data > 0
        ? t('beheer.medewerkers.pushOn', { count: push.data })
        : t('beheer.medewerkers.pushOff'),
    ]);
  }

  // Ook zonder account: de code maken legt het ontbrekende account alsnog aan.
  const kanCode = canReset && Boolean(employeeNumber);

  return (
    <Sect title={t('beheer.medewerkers.account')}>
      <DefinitionList items={rijen} />

      {kanCode ? (
        <>
          <div className="a-actions">
            {/*
              Eén knop en niet twee: "Nieuwe code" en "Pincode herstellen" zijn dezelfde aanvraag
              (een activatiecode maken zet ook de pincode terug). Het opschrift volgt wat de
              medewerker heeft: zonder pincode een nieuwe code, met pincode het herstel.
            */}
            <Btn
              variant="sec"
              size="sm"
              icon={<AdminIcon name={access.data?.hasPin ? 'rotateCcw' : 'qr'} />}
              disabled={issueCode.isPending}
              aria-busy={issueCode.isPending}
              onClick={() => issueCode.mutate()}
            >
              {access.data?.hasPin
                ? t('beheer.medewerkers.resetPin')
                : t('beheer.medewerkers.newCode')}
            </Btn>
          </div>
          <div className="a-hint">{t('beheer.medewerkers.codeHint')}</div>
        </>
      ) : null}

      {!employeeNumber ? <div className="a-hint">{t('access.noEmployeeNumber')}</div> : null}

      {canManageAccounts && status !== 'none' ? (
        <>
          <div className="a-actions">
            <Btn
              variant="sec"
              size="sm"
              icon={<AdminIcon name="key" />}
              testId="person-mfa-reset"
              disabled={resetMfa.isPending}
              onClick={() => {
                if (window.confirm(t('mfa.resetConfirm'))) resetMfa.mutate();
              }}
            >
              {t('mfa.reset')}
            </Btn>
          </div>
          <div className="a-hint">{t('mfa.resetHint')}</div>
        </>
      ) : null}

      {/*
        De code staat hier één keer: daarna bestaat hij alleen nog gehasht. In het paneel en niet in
        een venster erboven: een venster op een paneel sluit met één Escape allebei.
      */}
      {issuedCode ? (
        <div className="a-stack" data-testid="person-issued-code">
          <p className="a-muted">
            {t('access.codeFor', {
              name: issuedCode.displayName,
              number: issuedCode.employeeNumber ?? '—',
            })}{' '}
            {t('access.codeValidUntil', {
              date: new Date(issuedCode.expiresAt).toLocaleDateString(i18n.language),
            })}
          </p>
          <CodeDisplay code={issuedCode.code} label={t('access.codeTitle')} />
          <div className="a-actions">
            <Btn
              variant="sec"
              size="sm"
              icon={<AdminIcon name="printer" />}
              onClick={() => window.print()}
            >
              {t('access.print')}
            </Btn>
            <Btn variant="ghost" size="sm" onClick={() => setIssuedCode(null)}>
              {t('common.hide')}
            </Btn>
          </div>
        </div>
      ) : null}
      {melding.element}
    </Sect>
  );
}

const ACCESS_TONE: Record<AccessStatus['status'], BadgeTone> = {
  active: 'ok',
  pending_activation: 'attention',
  disabled: 'danger',
  none: 'neutral',
};

/** ACTIEF groen, WACHT OP ACTIVATIE oranje, GEEN ACCOUNT grijs (M1); geen toegang rood. */
export function AccessBadge({ status }: { status: AccessStatus['status'] }) {
  const { t } = useTranslation();
  return <Badge tone={ACCESS_TONE[status]}>{t(`access.statuses.${status}`)}</Badge>;
}

/**
 * "Uit dienst" (M2): nu of op een datum, met een reden die alleen HR ziet. Wie uit dienst is,
 * ziet hier "Opnieuw in dienst"; bij een tijdelijke kracht staat ook de einddatum hier.
 *
 * Tot BHR-2 een apart venster; de mockup zet het formulier in het paneel. De knop "Uit dienst
 * zetten" is de bevestiging: er gebeurt niets voor die geklikt is.
 */
export function PersonExitSection({
  personId,
  temporary,
  endDate,
  onChanged,
}: {
  personId: string;
  /** Een uitzendkracht of aannemer: dan staat de einddatum met "Verlengen" hier. */
  temporary: boolean;
  endDate: string | null;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const melding = useMelding();
  const queryClient = useQueryClient();
  const canManage = has('directory.person.manage');

  const [when, setWhen] = useState<'now' | 'date'>('now');
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const deactivation = useQuery({
    queryKey: ['directory', 'deactivation', personId],
    enabled: canManage,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/people/{personId}/deactivation', {
        params: { path: { personId } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as Deactivation;
    },
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['identity'] });
    await queryClient.invalidateQueries({ queryKey: ['directory'] });
    onChanged();
  };

  const reactivate = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/people/{personId}/reactivate', {
        params: { path: { personId } },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      melding.ok(t('access.reactivated'));
      await refresh();
    },
    // Een tijdelijke zonder geldige einddatum komt niet terug; de reden staat in de fout.
    onError: (fout) =>
      melding.fout(
        (fout as { errors?: { message: string }[] }).errors?.[0]?.message ??
          t('access.reactivateFailed'),
      ),
  });

  const submit = async () => {
    setBusy(true);
    try {
      const { error } = await api.POST('/api/v1/people/{personId}/deactivate', {
        params: { path: { personId } },
        body: {
          effective: when === 'now' ? 'now' : date,
          reason: reason.trim() || undefined,
        } as never,
      });
      if (error) {
        melding.fout(t('access.deactivateFailed'));
        return;
      }
      melding.ok(when === 'now' ? t('access.deactivated') : t('access.scheduled'));
      setReason('');
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  // Wie alleen mag kijken, ziet bij een tijdelijke kracht de einddatum (zonder "Verlengen").
  if (!canManage) {
    return temporary ? (
      <Sect title={t('beheer.medewerkers.exit')}>
        <TemporaryEnd personId={personId} endDate={endDate} onSaved={onChanged} />
      </Sect>
    ) : null;
  }
  const exit = deactivation.data;
  if (!exit) return melding.element;

  const datum = (value: string | null) =>
    value ? formatDate(value.length === 10 ? `${value}T12:00:00` : value) : '—';

  const feiten: [string, ReactNode][] = [];
  if (exit.status === 'deactivated') {
    feiten.push([t('beheer.medewerkers.exitSince'), datum(exit.deactivatedAt)]);
  } else if (exit.plannedExitDate) {
    feiten.push([
      t('access.plannedExit'),
      <Badge key="gepland" tone="attention">
        {datum(exit.plannedExitDate)}
      </Badge>,
    ]);
  }
  if (exit.reason) feiten.push([t('access.reason'), exit.reason]);

  return (
    <Sect title={t('beheer.medewerkers.exit')}>
      {temporary ? (
        <TemporaryEnd personId={personId} endDate={endDate} onSaved={onChanged} />
      ) : null}

      {feiten.length > 0 ? <DefinitionList items={feiten} /> : null}

      {exit.status === 'active' ? (
        <>
          <div className="a-muted">{t('beheer.medewerkers.exitIntro')}</div>
          <Row>
            <div className="a-field">
              <div className="a-label" id={`uitdienst-${personId}`}>
                {t('access.when')}
              </div>
              <Segmented
                labelledBy={`uitdienst-${personId}`}
                options={[
                  { value: 'now' as const, label: t('access.whenNow') },
                  { value: 'date' as const, label: t('access.whenDate') },
                ]}
                value={when}
                onChange={setWhen}
              />
            </div>
            {when === 'date' ? (
              <Field label={t('beheer.medewerkers.exitDate')} hint={t('access.lastWorkingDayHint')}>
                {(props) => (
                  <Input
                    {...props}
                    type="date"
                    value={date}
                    onChange={(event) => setDate(event.target.value)}
                  />
                )}
              </Field>
            ) : null}
          </Row>
          <Field label={t('access.reason')} hint={t('access.reasonHint')}>
            {(props) => (
              <Input
                {...props}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            )}
          </Field>
          <div>
            <Btn
              variant="danger-sec"
              size="sm"
              disabled={busy || (when === 'date' && date === '')}
              aria-busy={busy}
              onClick={() => void submit()}
            >
              {t('beheer.medewerkers.deactivate')}
            </Btn>
          </div>
        </>
      ) : (
        <div>
          <Btn
            variant="sec"
            size="sm"
            disabled={reactivate.isPending}
            aria-busy={reactivate.isPending}
            onClick={() => reactivate.mutate()}
          >
            {t('beheer.medewerkers.reactivate')}
          </Btn>
        </div>
      )}
      {melding.element}
    </Sect>
  );
}

/**
 * Een tijdelijk account verlengen (Stap 31). De einddatum weghalen kan niet: een uitzendkracht
 * zonder einddatum is precies het account dat jaren blijft openstaan.
 */
function TemporaryEnd({
  personId,
  endDate,
  onSaved,
}: {
  personId: string;
  endDate: string | null;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const melding = useMelding();
  const queryClient = useQueryClient();
  const [value, setValue] = useState(endDate ?? '');
  const [error, setError] = useState<string | undefined>();
  const extend = useMutation({
    mutationFn: async () => {
      const { error: fout } = await api.PATCH('/api/v1/people/{personId}', {
        params: { path: { personId } },
        body: { plannedExitDate: value } as never,
      });
      if (fout) throw fout;
    },
    onSuccess: async () => {
      setError(undefined);
      await queryClient.invalidateQueries({ queryKey: ['directory', 'person', personId] });
      melding.ok(t('temporary.extended'));
      onSaved();
    },
    onError: (fout) =>
      setError(
        (fout as { errors?: { message: string }[] }).errors?.[0]?.message ?? t('temporary.failed'),
      ),
  });
  const kanBeheren = has('directory.person.manage');

  return (
    <div className="a-stack" data-testid="person-temporary">
      <Row>
        <Field
          label={t('temporary.endDate')}
          hint={t('beheer.medewerkers.endDateHint')}
          error={error}
        >
          {(props) => (
            <Input
              {...props}
              type="date"
              value={value}
              disabled={!kanBeheren}
              onChange={(event) => setValue(event.target.value)}
            />
          )}
        </Field>
        {kanBeheren ? (
          <div className="a-field" style={{ justifyContent: 'flex-end' }}>
            <div>
              <Btn
                variant="sec"
                size="sm"
                disabled={!value || value === endDate || extend.isPending}
                aria-busy={extend.isPending}
                onClick={() => extend.mutate()}
              >
                {t('temporary.extend')}
              </Btn>
            </div>
          </div>
        ) : null}
      </Row>
      {melding.element}
    </div>
  );
}
